import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subject, throwError as observableThrowError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Company } from 'src/app/core/models/company.model';
import { StorageService } from './storage.service';

interface UserData {
  token: {
    access_token: string;
    token_type: string;
  };
  user: {
    admin: boolean;
    email: string;
    first_name: string;
    id: number;
    last_name: string;
    show_customer_data: boolean;
    super_manager: boolean;
  };
  company?: Company;
}

@Injectable()
export class LoginService {
  private tokensUrl = `${environment.apiUrl}/admin/tokens`;
  private signupUrl = `${environment.apiUrl}/admin/signup`;
  private onboardingUrl = `${environment.apiUrl}/admin/companies/onboarding`;
  private userChangedSubject = new Subject<void>();
  public isUserLoggedInByCredentials = false;

  constructor(
    private readonly http: HttpClient,
    private readonly storageService: StorageService
  ) {}

  private handleError(error: HttpErrorResponse | any) {
    let errMsg: string;
    if (error instanceof HttpErrorResponse) {
      const body = error;
      const err = body.error || JSON.stringify(body);
      errMsg = `${error.status} - ${error.statusText || ''} ${err}`;
    } else {
      errMsg = error.message ? error.message : error.toString();
    }

    return observableThrowError(errMsg);
  }

  private handleSuccess(response: UserData) {
    this.isUserLoggedInByCredentials = true;
    this.storageService.setToken(response.token.access_token);
    const user = response.company ? { ...response.user, company: response.company } : response.user;
    this.storageService.setCurrentUser(user as any);
    this.userChangedSubject.next();
  }

  login(email: string, password: string) {
    const body = JSON.stringify({ email, password });
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(this.tokensUrl, body, { headers }).pipe(
      map((r: any) => r.data),
      map(this.handleSuccess.bind(this)),
      catchError(this.handleError)
    );
  }

  logout() {
    this.storageService.clear();
    window.location.reload();
  }

  signup(user: any) {
    return this.http.post(this.signupUrl, user).pipe(
      map((r: any) => r.data),
      map(this.handleSuccess.bind(this)),
      catchError(this.handleError)
    );
  }

  getUserChangedSubject() {
    return this.userChangedSubject;
  }

  getRegistrationData(token: string) {
    return this.http
      .get(`${this.onboardingUrl}`, {
        params: {
          token
        }
      })
      .pipe(
        map((r: any) => this.handleSuccess(r as UserData)),
        catchError(this.handleError)
      );
  }
}
